import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { DeUiIconComponent } from './ui-icon.component.js';

/**
 * Debouncing lives here rather than in core: it is a UI concern, and engine
 * tests should never have to wait on a timer.
 */
@Component({
  selector: 'de-search-input',
  standalone: true,
  imports: [DeUiIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="de-search">
      <span class="de-search-icon"><de-ui-icon name="search" /></span>
      <input
        type="search"
        role="searchbox"
        class="de-search-input"
        [value]="draft()"
        [attr.placeholder]="placeholder()"
        [attr.aria-label]="placeholder()"
        (input)="onInput($event)"
        (keydown.escape)="clear($event)"
      />
      @if (draft() !== '') {
        <button type="button" class="de-search-clear" aria-label="Clear search" (click)="clear()">
          <de-ui-icon name="close" />
        </button>
      }
    </div>
  `,
})
export class DeSearchInputComponent {
  readonly value = input<string>('');
  readonly placeholder = input<string>('Search');
  readonly debounce = input<number>(150);
  readonly valueChange = output<string>();

  protected readonly draft = signal('');
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    // Keep in step when the store resets the query, e.g. on folder navigation.
    effect(() => this.draft.set(this.value()));
    inject(DestroyRef).onDestroy(() => clearTimeout(this.timer));
  }

  protected onInput(event: Event): void {
    const next = (event.target as HTMLInputElement).value;
    this.draft.set(next);
    this.emit(next);
  }

  protected clear(event?: Event): void {
    event?.stopPropagation();
    if (this.draft() === '') return;
    this.draft.set('');
    this.emit('');
  }

  private emit(next: string): void {
    clearTimeout(this.timer);
    const wait = this.debounce();
    if (wait <= 0) {
      this.valueChange.emit(next);
      return;
    }
    this.timer = setTimeout(() => this.valueChange.emit(next), wait);
  }
}
